import React, { useState, useEffect, useRef, useMemo } from "react";
import { Playlist, ScheduleType } from "../../types/index";
import { getRotatedItems } from "../../utils/rotationUtils";
import { PrayerTimes } from "../../types/PrayerTypes";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import { useEnhancedPlaylistScheduler } from "../../hooks/useEnhancedPlaylistScheduler";
import { getNextPrayerTime } from "../../utils/prayerTimeUtils";
import { getJummahDisplayInfo, formatTimeDisplay } from "../../utils/timeUtils";
import { formatHijriDate } from "../../utils/dateUtils";
import {
  ScreenData,
  shouldShowCategory,
  getUpcomingSalahLookaheadDays,
} from "../../types/ScreenTypes";
import "./SlideshowPage.css";
import BlankScreen from "../elements/BlankScreen";
import MakroohWarning from "../elements/MakroohWarning";
import { DigitalAssetType } from "../../types/DigitalAssetTypes";
import UpcomingSalahChangesSlideshow from "./UpcomingSalahChangesSlideshow";
import UpcomingEventsWidget from "./UpcomingEventsWidget";
import LabbaikSlide from "./LabbaikSlide";
import useLabbaikItems, { labbaikSlideshowItems } from "../../hooks/useLabbaikItems";
import { fetchNext7DaysPrayerTimes } from "../../services/api";
import CachedImage from "../shared/CachedImage";
import VideoPlayerFactory from "../shared/VideoPlayerFactory";
import { getCurrentTimeInTimezone } from "../../utils/timeUtils";
import CurrentTimeBar from "../elements/CurrentTimeBar";
import { VIDEO_BUFFER, YOUTUBE_VIDEO_BUFFER } from "../../constants/general";
import {
  getUrgentInjectCadence,
  mergeInjectItems,
  sortByPriorityDesc,
  splitUrgentInjectPlaylists,
} from "../../utils/urgentInjectUtils";
import { hasUpcomingSalahChanges } from "../../utils/upcomingSalahChangesEligibility";

type ImageDisplayMode = "contain" | "fill" | "cover";

interface SlideshowPageProps {
  playlists: Playlist[];
  prayerTimes: PrayerTimes;
  tomorrowPrayerTimes?: PrayerTimes | null;
  masjidId: number;
  screenData: ScreenData;
  onComplete: () => void;
  forceContinuousLoop?: boolean;
  prayerState: PrayerState;
  prevFridayJummah1: string | null;
  prevFridayJummah2: string | null;
  prevFridayJummah3: string | null;
  prevFridayJummah4: string | null;
  upcomingSalahChangesEligible?: boolean;
}

export default function SlideshowPage({
  playlists,
  prayerTimes,
  tomorrowPrayerTimes,
  masjidId,
  screenData,
  onComplete,
  forceContinuousLoop = false,
  prayerState,
  prevFridayJummah1,
  prevFridayJummah2,
  prevFridayJummah3,
  prevFridayJummah4,
  upcomingSalahChangesEligible = true,
}: SlideshowPageProps) {
  // Transform scheduleType from string to enum value (memoized)
  const transformedPlaylists = useMemo(
    () =>
      playlists.map((p) => ({
        ...p,
        scheduleType:
          typeof p.scheduleType === "string"
            ? ScheduleType[
                p.scheduleType as unknown as keyof typeof ScheduleType
              ]
            : p.scheduleType,
      })),
    [playlists],
  );

  // Configure scheduler behavior
  const schedulerConfig = useMemo(
    () => ({
      enablePriorityOverride: true,
      enableInterruption: prayerState.shouldPauseContent,
      defaultMinimumRunDuration: 1, // 1 minute
      defaultMaximumRunDuration: 60, // 1 hour
      evaluationInterval: 30000, // 30 seconds
      enableLogging: process.env.NODE_ENV === "development",
    }),
    [prayerState.shouldPauseContent],
  );

  const {
    schedulerResult,
    isLoading: schedulerLoading,
    error,
  } = useEnhancedPlaylistScheduler({
    playlists: transformedPlaylists as any,
    prayerTimes,
    enableAdvancedScheduling: true,
    configuration: schedulerConfig,
  });

  const activePlaylists = (schedulerResult?.activePlaylists as any[]) || [];
  const labbaikItems = useLabbaikItems(masjidId, screenData.showLabbaik);

  // Slideshow state
  const [allItems, setAllItems] = useState<any[]>([]);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [imageOrientation, setImageOrientation] = useState<
    "landscape" | "portrait" | "square" | null
  >(null);
  const [next7DaysTimetable, setNext7DaysTimetable] = useState<PrayerTimes[]>(
    [],
  );
  const [isFetchingTimetable, setIsFetchingTimetable] = useState(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [displayMode, setDisplayMode] = useState<ImageDisplayMode>(
    screenData.noticeboardSettings?.slideshowPosterFitMode === "cover"
      ? "cover"
      : screenData.noticeboardSettings?.slideshowPosterFitMode === "fill"
        ? "fill"
        : "contain",
  );
  const [showDebug] = useState(process.env.NODE_ENV === "development");

  const [nowTime, setNowTime] = useState(() => new Date());

  const timerRef = useRef<number | null>(null);
  const intervalRef = useRef<number | null>(null);
  const onCompleteRef = useRef(onComplete);
  const itemsSnapshotRef = useRef<any[] | null>(null);
  const cycleActiveRef = useRef(false);
  const videoDurationRef = useRef<number | null>(null);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Online/offline listener (single effect)
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setNowTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  function formatClockTime(date: Date, timeZone: string) {
    return date.toLocaleTimeString("en-GB", {
      hour12: true,
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone, // Use the masjid's time zone
    });
  }

  // Build slideshow items from active playlists
  // Replace the existing useEffect that builds slideshow items with this:

  useEffect(() => {
    // Wait for scheduler to finish loading
    if (schedulerLoading) {
      return;
    }

    // 🔧 FIX: Only rebuild items if we don't have a current cycle running
    if (
      cycleActiveRef.current &&
      itemsSnapshotRef.current &&
      itemsSnapshotRef.current.length > 0
    ) {
      return;
    }

    const items: any[] = [];

    // All playlists from the scheduler are regular playlists
    // (Virtual "Events" playlist with Id=-1 has been decommissioned server-side)
    const allEligiblePlaylists = activePlaylists.map((ap: any) => ap.playlist);

    const now = new Date();
    const { regularPlaylists, injectPlaylists: urgentInjectPlaylists } =
      splitUrgentInjectPlaylists(allEligiblePlaylists, now);

    const regularItems: any[] = [];
    const injectItems: any[] = [];

    // Sort by priority and add items
    const sortedRegular = sortByPriorityDesc(regularPlaylists);

    for (const pl of sortedRegular) {
      if (!pl?.items?.length) continue;
      const sortedItems = pl.items
        .slice()
        .sort(
          (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0),
        );

      // Rotation mode: daily for date-based, weekly for time/prayer-based schedules
      // When enabled, shows only one item per rotation period using modulo
      let itemsToProcess = sortedItems;
      if (pl.isDailyRotation && sortedItems.length > 0) {
        itemsToProcess = getRotatedItems(
          pl,
          sortedItems,
          prayerTimes,
          "[SlideshowPage]",
        );
      }

      // Apply category filtering (showEventPosters, showFuneralNotices, showInformationPosters)
      const filteredItems = itemsToProcess.filter((item: any) => {
        const category = item.eventCategory;
        // Items without a category always pass through
        if (!category) return true;
        return shouldShowCategory(screenData, category);
      });

      regularItems.push(
        ...filteredItems.map((item: any) => ({
          ...item,
          playlistId: pl.id,
          playlistName: pl.name,
          isEvent: false,
          isIslamicEvent: !!pl.islamicEvent,
        })),
      );
    }

    const sortedInject = sortByPriorityDesc(urgentInjectPlaylists);

    for (const pl of sortedInject) {
      if (!pl?.items?.length) continue;

      const sortedItems = pl.items
        .slice()
        .sort(
          (a: any, b: any) => (a.displayOrder || 0) - (b.displayOrder || 0),
        );

      const filteredItems = sortedItems.filter((item: any) => {
        const category = item.eventCategory;
        if (!category) return true;
        return shouldShowCategory(screenData, category);
      });

      injectItems.push(
        ...filteredItems.map((item: any) => ({
          ...item,
          duration: Math.max(
            item.duration || 10,
            pl.urgentMinShowSeconds || 10,
          ),
          playlistId: pl.id,
          playlistName: pl.name,
          isEvent: false,
          isIslamicEvent: !!pl.islamicEvent,
          isUrgentInjectItem: true,
        })),
      );
    }

    const injectCadence = getUrgentInjectCadence(sortedInject);

    items.push(...mergeInjectItems(regularItems, injectItems, injectCadence));
    items.push(...labbaikSlideshowItems(labbaikItems));

    // 3. Independent panels
    if (screenData.showNextSalahChanges && upcomingSalahChangesEligible) {
      items.push({
        id: "upcoming-salah-changes",
        assetType: "upcomingSalahChanges",
        duration: screenData.nextSalahChangesDuration || 10,
        displayOrder: 999,
        isSpecialItem: true,
      });
    }

    // Add upcoming events widget if enabled
    if (screenData.showUpcomingEvents) {
      items.push({
        id: "upcoming-events-widget",
        assetType: "upcomingEventsWidget",
        duration: screenData.upcomingEventsDuration || 15,
        displayOrder: 997,
        isSpecialItem: true,
      });
    }

    itemsSnapshotRef.current = items;
    setAllItems(items);

    // 🔧 FIX: Only reset index if we're not mid-cycle
    if (!cycleActiveRef.current) {
      setCurrentItemIndex(0);
    }

    setIsLoading(false);
  }, [
    activePlaylists,
    schedulerLoading,
    screenData.showEvents,
    screenData.showEventPosters,
    screenData.showFuneralNotices,
    screenData.showInformationPosters,
    screenData.showAnnouncements,
    screenData.showClassEnrollments,
    screenData.showNextSalahChanges,
    screenData.nextSalahChangesDuration,
    upcomingSalahChangesEligible,
    labbaikItems,
  ]);

  // Start timers once items ready
  useEffect(() => {
    if (
      isLoading ||
      schedulerLoading ||
      !itemsSnapshotRef.current ||
      !itemsSnapshotRef.current.length ||
      prayerState.shouldPauseContent ||
      prayerState.isCountdownActive
    )
      return;

    // ensure we start a new cycle only once
    if (!cycleActiveRef.current) {
      cycleActiveRef.current = true;
      startItemTimer(0);
    }

    return () => {
      // cleanup on unmount or re-run
      cycleActiveRef.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    schedulerLoading,
    prayerState.shouldPauseContent,
    prayerState.isCountdownActive,
  ]);

  // Pause/resume
  useEffect(() => {
    const paused =
      prayerState.shouldPauseContent || prayerState.isCountdownActive;
    if (paused) {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    } else if (allItems.length && !timerRef.current) {
      startItemTimer(currentItemIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prayerState.shouldPauseContent,
    prayerState.isCountdownActive,
    allItems.length,
  ]);

  // Return to prayer view if no items
  useEffect(() => {
    if (
      !isLoading &&
      !schedulerLoading &&
      allItems.length === 0 &&
      !isFetchingTimetable &&
      !screenData.showNextSalahChanges
    ) {
      const h = setTimeout(() => {
        onCompleteRef.current && onCompleteRef.current();
      }, 0);
      return () => clearTimeout(h);
    }
  }, [
    isLoading,
    schedulerLoading,
    allItems.length,
    isFetchingTimetable,
    screenData.showNextSalahChanges,
  ]);

  // Progress bar (smooth)
  useEffect(() => {
    if (isLoading || allItems.length === 0) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    setElapsedTime(0);
    intervalRef.current = window.setInterval(() => {
      setElapsedTime((t) => t + 0.1);
    }, 100);
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentItemIndex, isLoading, allItems.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Replace the startItemTimer function

  function startItemTimer(index: number) {
    // Clear any previous timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const items = itemsSnapshotRef.current || [];
    if (!items.length) {
      console.log("[SlideshowPage] No items in snapshot, completing slideshow");
      cycleActiveRef.current = false;
      onCompleteRef.current?.();
      return;
    }

    // If index beyond end -> end cycle
    if (index >= items.length) {
      if (forceContinuousLoop && items.length > 0) {
        console.log("[SlideshowPage] Reached end of items, restarting cycle");
        startItemTimer(0);
      } else {
        console.log(
          "[SlideshowPage] Reached end of items, completing slideshow cycle",
        );
        cycleActiveRef.current = false;
        onCompleteRef.current?.();
      }
      return;
    }

    const item = items[index];
    setCurrentItemIndex(index);

    const isVideo = item.assetType?.toLowerCase() === "video";

    // For videos, set a visual timer but don't actually use it for navigation
    if (isVideo) {
      console.log(
        `[SlideshowPage] Video item - duration handled by player callbacks`,
      );
      // Set a duration for the progress bar
      setSecondsLeft(item.duration || 30);
      setElapsedTime(0);

      // Start progress tick for UI only
      intervalRef.current = window.setInterval(() => {
        setElapsedTime((t) => t + 0.1);
      }, 100);

      // NO timeout for videos - handleVideoEnded will advance to next item
      return;
    }

    // For non-video items, use duration timer as before
    const duration = Math.max(1, item.duration || 10);
    setSecondsLeft(duration);
    setElapsedTime(0);

    // Start progress tick
    intervalRef.current = window.setInterval(() => {
      setElapsedTime((t) => t + 0.1);
    }, 100);

    timerRef.current = window.setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (index >= items.length - 1) {
        if (forceContinuousLoop && items.length > 0) {
          console.log("[SlideshowPage] Final item completed, restarting cycle");
          startItemTimer(0);
        } else {
          console.log("[SlideshowPage] Final item completed, ending slideshow");
          cycleActiveRef.current = false;
          onCompleteRef.current?.();
        }
      } else {
        console.log("[SlideshowPage] Moving to next item");
        startItemTimer(index + 1);
      }
    }, duration * 1000);
  }

  const currentItem = allItems[currentItemIndex];

  // Next 7 days timetable
  useEffect(() => {
    if (!screenData.showNextSalahChanges || !upcomingSalahChangesEligible) {
      setNext7DaysTimetable([]);
      return;
    }

    let cancelled = false;

    const fetchTimetables = async () => {
      try {
        setIsFetchingTimetable(true);
        const lookaheadDays = getUpcomingSalahLookaheadDays(screenData);

        // Use the new helper function
        const data = await fetchNext7DaysPrayerTimes(masjidId, lookaheadDays);

        if (!cancelled) {
          setNext7DaysTimetable(data);
        }
      } catch (error) {
        console.error("Error fetching 7-day timetables:", error);
        if (!cancelled) {
          setNext7DaysTimetable([]);
        }
      } finally {
        if (!cancelled) {
          setIsFetchingTimetable(false);
        }
      }
    };

    fetchTimetables();

    return () => {
      cancelled = true;
    };
  }, [
    screenData.showNextSalahChanges,
    screenData.upcomingSalahLookaheadDays,
    upcomingSalahChangesEligible,
    masjidId,
  ]);

  const effectiveUpcomingSalahChangesEligible = useMemo(() => {
    if (!screenData.showNextSalahChanges) {
      return false;
    }

    if (!screenData.hideUpcomingSalahWhenNoChanges) {
      return true;
    }

    // Prefer controller precomputed truth; fall back to local timetable check.
    if (!upcomingSalahChangesEligible) {
      return false;
    }

    return hasUpcomingSalahChanges({
      timetables: next7DaysTimetable,
      prayerTimes,
      prevFridayJummah1,
      prevFridayJummah2,
      prevFridayJummah3,
      prevFridayJummah4,
    });
  }, [
    screenData.showNextSalahChanges,
    screenData.hideUpcomingSalahWhenNoChanges,
    upcomingSalahChangesEligible,
    next7DaysTimetable,
    prayerTimes,
    prevFridayJummah1,
    prevFridayJummah2,
    prevFridayJummah3,
    prevFridayJummah4,
  ]);

  // Format Islamic date using mosque's preferred names and format
  const formatIslamicDate = (dateValue: any): string => {
    if (typeof dateValue === "string") {
      return dateValue;
    }

    if (dateValue && typeof dateValue === "object") {
      const day = dateValue.Day || dateValue.day || "";
      const monthName = dateValue.MonthName || dateValue.monthName || "";
      const year = dateValue.Year || dateValue.year || "";

      if (day && monthName && year) {
        const format = screenData.hijriDateFormat || "dd MMMM yyyy";
        return formatHijriDate(
          String(day),
          String(monthName),
          String(year),
          format,
          screenData.customizedNames,
        );
      }

      if (dateValue.FormattedDate) {
        return dateValue.FormattedDate;
      }
    }

    return "--";
  };

  // Memoize the formatted Islamic date
  const formattedIslamicDate = useMemo(() => {
    return formatIslamicDate(prayerState.currentIslamicDate);
  }, [prayerState.currentIslamicDate]);

  // Memoize the formatted Gregorian date
  const formattedGregorianDate = useMemo(() => {
    const today = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return today.toLocaleDateString("en-US", options);
  }, [prayerTimes.GregorianDate]);
  const slideshowDisabled =
    prayerState.shouldPauseContent || prayerState.isCountdownActive;
  const slideshowPosterFitMode =
    screenData.noticeboardSettings?.slideshowPosterFitMode ?? "auto";

  function handleVideoEnded() {
    if (timerRef.current) clearTimeout(timerRef.current);
    startItemTimer(currentItemIndex + 1);
  }

  function handleImageLoad(e: React.SyntheticEvent<HTMLImageElement, Event>) {
    const img = e.currentTarget;

    // ✅ Calculate aspect ratio with threshold for near-square images
    const aspectRatio = img.naturalWidth / img.naturalHeight;
    const SQUARE_THRESHOLD = 0.1; // 10% tolerance

    let orient: "landscape" | "portrait" | "square";

    if (Math.abs(aspectRatio - 1) < SQUARE_THRESHOLD) {
      // Near-square image (within 10% of 1:1 ratio)
      orient = "square";
    } else if (aspectRatio > 1) {
      orient = "landscape";
    } else {
      orient = "portrait";
    }

    setImageOrientation(orient);

    const screenOrient =
      window.innerHeight > window.innerWidth ? "portrait" : "landscape";

    // Event-specific filtering logic
    if (currentItem?.assetType?.toLowerCase() === "event") {
      const showLandscapeOnPortrait =
        screenData.showLandscapePosterOnPortrait ?? true;
      const showPortraitOnLandscape =
        screenData.showPortraitPosterOnLandscape ?? true;

      const skip =
        (screenOrient === "portrait" &&
          orient === "landscape" &&
          !showLandscapeOnPortrait) ||
        (screenOrient === "landscape" &&
          orient === "portrait" &&
          !showPortraitOnLandscape);

      if (skip) {
        const next = (currentItemIndex + 1) % allItems.length;
        setCurrentItemIndex(next);
        return;
      }
    }

    // Explicit mode override from backend screen settings.
    let mode: ImageDisplayMode;
    if (slideshowPosterFitMode !== "auto") {
      // "blur" is the slot slideshow's backdrop mode; full screen it means true shape
      mode =
        slideshowPosterFitMode === "blur"
          ? "contain"
          : (slideshowPosterFitMode as ImageDisplayMode);
    } else {
      // Orientation-aware fitting fallback:
      // Same orientation → fill (slight stretch, no bars, no cropping)
      // Different orientation or square → contain (bars accepted)
      if (orient === "square") {
        mode = "contain";
      } else if (orient === screenOrient) {
        mode = "fill";
      } else {
        mode = "contain";
      }
    }
    setDisplayMode(mode);

    console.log(
      `Image loaded: ${img.naturalWidth}x${img.naturalHeight}, ` +
        `aspect ratio: ${aspectRatio.toFixed(3)}, ` +
        `orientation: ${orient}, screen: ${screenOrient}, mode: ${mode}`,
    );
  }

  const now = getCurrentTimeInTimezone(screenData.timeZone);
  const currentTimeStr = `${now.getHours().toString().padStart(2, "0")}:${now
    .getMinutes()
    .toString()
    .padStart(2, "0")}`;

  const isVideo = currentItem?.assetType?.toLowerCase() === "video";
  const isYouTube =
    currentItem?.assetUrl?.includes("youtube.com") ||
    currentItem?.assetUrl?.includes("youtu.be");
  const buffer = isYouTube ? YOUTUBE_VIDEO_BUFFER : VIDEO_BUFFER;
  const effectiveDuration = isVideo
    ? (videoDurationRef.current ?? currentItem.duration ?? 30) + buffer
    : currentItem?.duration || 10;
  const progress = Math.min((elapsedTime / effectiveDuration) * 100, 100);

  const getJamaatTimes = (
    today: PrayerTimes,
    tomorrow: PrayerTimes | null,
    currentTimeStr: string,
    customizedNames: Record<string, string>,
  ) => {
    const formatJamaatTime = (t: string) =>
      !t || t === "--:--"
        ? t
        : formatTimeDisplay(t, screenData.use12HourFormat);
    const jummahInfo = getJummahDisplayInfo(
      new Date(),
      currentTimeStr,
      today,
      screenData,
    );
    return [
      {
        label: customizedNames.fajr || "Fajr",
        time: formatJamaatTime(
          getNextPrayerTime("fajr", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: jummahInfo.showJummah
          ? jummahInfo.jummahNumber > 1
            ? `${customizedNames.jummah || "Jumu'ah"} ${
                jummahInfo.jummahNumber
              }`
            : customizedNames.jummah || "Jumu'ah"
          : customizedNames.dhuhr || "Dhuhr",
        time: formatJamaatTime(
          jummahInfo.showJummah
            ? jummahInfo.jummahTime || "--:--"
            : getNextPrayerTime("dhuhr", today, tomorrow, currentTimeStr)
                .iqama || "--:--",
        ),
      },
      {
        label: customizedNames.asar || "Asar",
        time: formatJamaatTime(
          getNextPrayerTime("asar", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: customizedNames.maghrib || "Maghrib",
        time: formatJamaatTime(
          getNextPrayerTime("maghrib", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
      {
        label: customizedNames.isha || "Isha",
        time: formatJamaatTime(
          getNextPrayerTime("isha", today, tomorrow, currentTimeStr).iqama ||
            "--:--",
        ),
      },
    ];
  };

  if (prayerState.isScreenBlank) {
    return (
      <BlankScreen
        message={screenData.salahInProgressMessage}
        hijriDate={formattedIslamicDate}
        gregorianDate={formattedGregorianDate}
        screenData={screenData}
        showDateTime={true}
        backgroundUrl={screenData.blankScreenBackgroundUrl}
        countdownSecondsLeft={prayerState.blankScreenSecondsLeft}
        countdownTotalSeconds={prayerState.blankScreenTotalSeconds}
        countdownPrayer={prayerState.blankScreenPrayer}
      />
    );
  }

  if (effectiveUpcomingSalahChangesEligible && isFetchingTimetable) {
    return (
      <div className="slideshow-loading">
        <div>Loading upcoming prayer time changes...</div>
      </div>
    );
  }

  if (error) return <div className="slideshow-loading">Error: {error}</div>;

  if (schedulerLoading || isLoading)
    return <div className="slideshow-loading">Loading slideshow...</div>;

  if (!allItems.length) return null;

  if (currentItem && currentItem.assetType === "upcomingSalahChanges") {
    return (
      <UpcomingSalahChangesSlideshow
        timetables={next7DaysTimetable}
        customizedNames={screenData.customizedNames}
        duration={currentItem.duration}
        onComplete={() => startItemTimer(currentItemIndex + 1)}
        prayerTimes={prayerTimes}
        tomorrowPrayerTimes={tomorrowPrayerTimes || null}
        currentIndex={currentItemIndex}
        totalItems={allItems.length}
        elapsedTime={elapsedTime}
        totalDuration={getTotalSlideshowDuration(allItems)}
        screenData={screenData}
        prevFridayJummah1={prevFridayJummah1}
        prevFridayJummah2={prevFridayJummah2}
        prevFridayJummah3={prevFridayJummah3}
        prevFridayJummah4={prevFridayJummah4}
        hideWhenNoChanges={
          screenData.hideUpcomingSalahWhenNoChanges &&
          effectiveUpcomingSalahChangesEligible
        }
      />
    );
  }

  // Render upcoming events widget
  if (currentItem && currentItem.assetType === "upcomingEventsWidget") {
    return (
      <UpcomingEventsWidget
        masjidId={masjidId}
        duration={currentItem.duration || 15}
        onComplete={() => startItemTimer(currentItemIndex + 1)}
        screenData={screenData}
        primaryColor={screenData.primaryColor}
        secondaryColor={screenData.secondaryColor}
        accentColor={screenData.accentColor}
        currentIndex={currentItemIndex}
        totalItems={allItems.length}
        elapsedTime={elapsedTime}
        totalDuration={getTotalSlideshowDuration(allItems)}
        hideWhenNoEvents={screenData.hideUpcomingEventsWhenNoEvents}
      />
    );
  }

  const assetType = currentItem.assetType?.toLowerCase() || "";

  const getImageClassName = () => {
    if (displayMode === "fill") return "slideshow-image fill-mode";
    if (displayMode === "cover") return "slideshow-image stretch-mode";
    return "slideshow-image";
  };

  return (
    <div
      className={`slideshow-page${screenData.slideshowBackgroundUrl ? " slideshow-page--has-bg" : ""}`}
      style={
        {
          "--compact-primary-color": screenData.primaryColor || "#FFC107",
          "--theme-primary-color": screenData.primaryColor || "#FFC107",
          "--theme-secondary-color": screenData.secondaryColor || "#FF9800",
          "--theme-accent-color": screenData.accentColor || "#4CAF50",
        } as React.CSSProperties
      }
    >
      {screenData.slideshowBackgroundUrl && (
        <img
          src={screenData.slideshowBackgroundUrl}
          alt=""
          className="slideshow-bg-image"
          onError={(e) => (e.currentTarget.style.display = "none")}
        />
      )}
      <CurrentTimeBar
        screenData={screenData}
        prayerTimes={prayerTimes}
        tomorrowPrayerTimes={tomorrowPrayerTimes}
      />
      {!slideshowDisabled && (
        <>
          <div className="slideshow-content">
            {assetType === DigitalAssetType.Poster ? (
              <div className="slideshow-item image-item">
                <CachedImage
                  src={currentItem.assetUrl}
                  alt={currentItem.assetTitle || "Slideshow Image"}
                  className={getImageClassName()}
                  onLoad={handleImageLoad}
                  onError={() => startItemTimer(currentItemIndex + 1)}
                  assetType={assetType}
                />
                {showDebug && (
                  <div className="display-mode-controls">
                    <div className="mode-info">
                      {imageOrientation &&
                        `Image: ${imageOrientation}, Screen: ${
                          window.innerHeight > window.innerWidth
                            ? "portrait"
                            : "landscape"
                        }`}
                    </div>
                    <button
                      className={`mode-button ${
                        displayMode === "contain" ? "active" : ""
                      }`}
                      onClick={() => setDisplayMode("contain")}
                    >
                      Fit
                    </button>
                    <button
                      className={`mode-button ${
                        displayMode === "fill" ? "active" : ""
                      }`}
                      onClick={() => setDisplayMode("fill")}
                    >
                      Fill
                    </button>
                    <button
                      className={`mode-button ${
                        displayMode === "cover" ? "active" : ""
                      }`}
                      onClick={() => setDisplayMode("cover")}
                    >
                      Stretch
                    </button>
                  </div>
                )}
              </div>
            ) : assetType === DigitalAssetType.Video ? (
              <div className="slideshow-item video-item">
                <VideoPlayerFactory
                  key={`video-${
                    currentItem.id || currentItemIndex
                  }-${currentItem.assetUrl?.substring(0, 20)}`}
                  item={{
                    ...currentItem,
                    // Explicitly set required properties
                    id: currentItem.id,
                    assetId: currentItem.assetId || currentItem.id,
                    assetUrl: currentItem.assetUrl,
                    assetTitle: currentItem.assetTitle || "Video",
                    assetType: "video",
                    duration: currentItem.duration || 30,
                    videoAutoplay: currentItem.videoAutoplay !== false,
                    videoMuted: currentItem.videoMuted !== false,
                    videoLoop: currentItem.videoLoop === true,
                  }}
                  screenData={{
                    showPortraitPosterOnLandscape:
                      screenData.showPortraitPosterOnLandscape,
                    showLandscapePosterOnPortrait:
                      screenData.showLandscapePosterOnPortrait,
                    orientation: screenData.orientation,
                  }}
                  onEnded={() => {
                    console.log(
                      `[SlideshowPage] Video ended naturally: ${currentItem.assetTitle}`,
                    );
                    // Critical: Don't call startItemTimer immediately
                    setTimeout(() => {
                      if (timerRef.current) {
                        clearTimeout(timerRef.current);
                        timerRef.current = null;
                      }
                      startItemTimer(currentItemIndex + 1);
                    }, 100);
                  }}
                  onError={(error) => {
                    console.error(`[SlideshowPage] Video error: ${error}`);
                    setTimeout(() => {
                      if (timerRef.current) {
                        clearTimeout(timerRef.current);
                        timerRef.current = null;
                      }
                      startItemTimer(currentItemIndex + 1);
                    }, 100);
                  }}
                  onTimeUpdate={(current, duration) => {
                    setElapsedTime(current);
                    videoDurationRef.current = duration;
                  }}
                  className="slideshow-video-player"
                />
              </div>
            ) : assetType === "labbaik" ? (
              <div className="slideshow-item labbaik-item">
                <LabbaikSlide
                  item={currentItem.labbaik}
                  colours={screenData}
                />
              </div>
            ) : assetType === "html" ? (
              <div className="slideshow-item html-item">
                <div
                  className="slideshow-html-content"
                  dangerouslySetInnerHTML={{ __html: currentItem.assetUrl }}
                />
              </div>
            ) : (
              <div className="slideshow-item unknown-item">
                <div className="unknown-content">
                  Unknown content type: {currentItem.assetType}
                </div>
              </div>
            )}
          </div>

          <div className="slideshow-jamaat-footer">
            <div
              className="footer-progress"
              style={{ width: `${progress}%` }}
            ></div>
            <div className="slideshow-counter">
              {currentItemIndex + 1} / {allItems.length}&nbsp;
            </div>
            {getJamaatTimes(
              prayerTimes,
              tomorrowPrayerTimes || null,
              currentTimeStr,
              screenData.customizedNames,
            ).map((p) => {
              // Split time and period (am/pm)
              const match = p.time.match(/^(\d{1,2}:\d{2})(am|pm)?$/i);
              const mainTime = match ? match[1] : p.time;
              const period = match && match[2] ? match[2].toLowerCase() : "";

              return (
                <span key={p.label} className="jamaat-time">
                  <span className="jamaat-time-label">{p.label}</span>
                  <strong>
                    {mainTime}
                    {/* {period && (
                      <>
                        <span className="period">{period}</span>
                      </>
                    )} */}
                  </strong>
                </span>
              );
            })}
          </div>
        </>
      )}

      {prayerState.isMakroohTime &&
        screenData.pausePlaylistsDuringMakrooh !== false && (
          <MakroohWarning
            reason={prayerState.makroohReason}
            customizedNames={{
              sunrise: screenData.customizedNames.sunrise,
              noon: screenData.customizedNames.noon,
              sunset: screenData.customizedNames.sunset,
            }}
            duration={{
              sunrise: screenData.sunriseMakroohPeriod,
              zawwal: screenData.zawwalMakroohPeriod,
              sunset: screenData.sunsetMakroohPeriod,
            }}
            secondsLeft={prayerState.makroohSecondsLeft}
            showTimer={screenData.showMakroohTimer ?? true}
            countdownLabel={screenData.makroohTimerLabel ?? "Time Remaining:"}
          />
        )}

      {!isOnline && showDebug && (
        <div className="offline-indicator">Offline Mode</div>
      )}

    </div>
  );
}

function getTotalSlideshowDuration(items: any[]): number {
  return items.reduce((t, i) => t + (i.duration || 10), 0);
}
